import { useEffect, useRef, useState } from "react";
import { Settings2 } from "lucide-react";
import { getAmountOrder, setAmountOrder, useAmountOrder, type AmountOrder } from "@/lib/amount-order";
import { cn } from "@/lib/utils";

// Header-level UI-preferences popover — lives next to the admin identity
// pill (top right), not tied to any one page. First (only, so far) setting:
// which line `Amount` (components/amount.tsx) puts on top when it renders a
// USD estimate. Plain custom popover rather than a Radix primitive — no
// dropdown-menu/popover package is installed yet and this is a single
// lightweight panel, not worth the new dependency. Mirrors
// apps/merchant/src/components/settings-popover.tsx.

const ORDER_OPTIONS: Array<{ value: AmountOrder; label: string; description: string }> = [
  { value: "crypto-first", label: "Crypto first", description: "Crypto amount on top, USD estimate below (default)." },
  { value: "fiat-first", label: "USD first", description: "USD estimate on top, crypto amount below." },
];

export function SettingsPopover() {
  const [open, setOpen] = useState(false);
  const order = useAmountOrder();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Display settings"
        aria-expanded={open}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Settings2 className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-72 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-lg">
          <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Amount display
          </div>
          <div className="space-y-1">
            {ORDER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  if (getAmountOrder() !== option.value) setAmountOrder(option.value);
                  setOpen(false);
                }}
                className={cn(
                  "w-full rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                  order === option.value ? "bg-accent" : "hover:bg-accent/50",
                )}
              >
                <div className="font-medium">{option.label}</div>
                <div className="text-xs text-muted-foreground">{option.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
